import { Link } from "react-router-dom";
import { Mail, ArrowRight, Inbox, MapPin } from "lucide-react";
import { useLang } from "@/lang";
import { MaskLines, Reveal, Crosshair } from "@/components/Reveal";
import { EMAIL, EMAIL_SALES } from "@/content";

const HERO_BG =
    "https://images.pexels.com/photos/10871585/pexels-photo-10871585.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const NET_IMG =
    "https://images.unsplash.com/photo-1615774925655-a0e97fc85c14?q=85&w=1200&auto=format&fit=crop";

function Band() {
    const { c } = useLang();
    return (
        <section className="relative flex min-h-[70svh] items-end overflow-hidden" data-testid="career-hero">
            <div className="absolute inset-0 -z-10">
                <img src={HERO_BG} alt="" className="h-full w-full scale-110 object-cover" />
                <div className="absolute inset-0 bg-ink/60" />
                <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-transparent to-ink" />
            </div>
            <div className="container-x pb-16 pt-40">
                <Reveal>
                    <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky">
                        <Crosshair className="text-sky" />
                        {c.career.eyebrow}
                    </div>
                </Reveal>
                <h1 className="mt-6 text-[clamp(2.8rem,8vw,7rem)] font-light leading-[1.03] tracking-[-0.02em] text-paper">
                    <MaskLines mount lines={c.career.lines} step={0.13} delay={0.2} />
                </h1>
                <Reveal delay={0.4}>
                    <p className="mt-6 max-w-xl text-base leading-relaxed text-paper/75 sm:text-lg">
                        {c.career.sub}
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

export default function Career() {
    const { c } = useLang();
    return (
        <>
            <Band />

            <section className="container-x grid gap-14 py-28 md:grid-cols-12 md:py-36" data-testid="career-intro">
                <div className="md:col-span-6">
                    <Reveal>
                        <h2 className="text-4xl font-light tracking-tight text-ink sm:text-5xl">
                            {c.career.introTitle}
                        </h2>
                        <p className="mt-8 text-base leading-relaxed text-ink-soft sm:text-lg" dangerouslySetInnerHTML={{ __html: c.career.introBody }} />
                    </Reveal>
                </div>
                <div className="md:col-span-5 md:col-start-8">
                    <Reveal delay={0.1}>
                        <div className="rounded-3xl border border-line bg-paper p-8" data-testid="career-mgmt">
                            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sky-text">
                                {c.career.mgmtTitle}
                            </p>
                            <p className="mt-5 text-base leading-relaxed text-ink-soft">
                                {c.career.mgmtBody}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="container-x pb-28 md:pb-36" data-testid="career-look">
                <Reveal>
                    <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky-text">
                        <span className="h-px w-10 bg-sky-text/50" />
                        {c.career.lookLabel}
                    </div>
                </Reveal>
                <div className="mt-12 border-t border-line">
                    {c.career.look.map((v, i) => (
                        <Reveal key={v.t} delay={i * 0.06}>
                            <div className="grid gap-4 border-b border-line py-10 md:grid-cols-12 md:items-baseline" data-testid={`career-look-${i + 1}`}>
                                <span className="font-light text-5xl text-mist/70 md:col-span-1">0{i + 1}</span>
                                <h3 className="text-2xl font-light tracking-tight text-ink md:col-span-4 sm:text-3xl">
                                    {v.t}
                                </h3>
                                <p className="max-w-xl text-base leading-relaxed text-ink-soft md:col-span-6">
                                    {v.d}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="container-x pb-28 md:pb-36" data-testid="career-network">
                <div className="grid items-center gap-14 lg:grid-cols-2">
                    <div>
                        <Reveal>
                            <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky-text">
                                <span className="h-px w-10 bg-sky-text/50" />
                                {c.career.netLabel}
                            </div>
                        </Reveal>
                        <MaskLines
                            lines={[c.career.netTitle]}
                            className="mt-6 block text-4xl font-light tracking-tight text-ink sm:text-5xl"
                        />
                        <Reveal delay={0.15}>
                            <p className="mt-8 max-w-lg text-base leading-relaxed text-ink-soft sm:text-lg">
                                {c.career.netBody}
                            </p>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <div className="mt-10 flex flex-wrap gap-2.5">
                                {c.career.netChips.map((chip) => (
                                    <span
                                        key={chip}
                                        className="flex items-center gap-2 rounded-full border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft"
                                        data-testid="career-network-chip"
                                    >
                                        <MapPin className="h-3 w-3 shrink-0 text-sky-text" />
                                        {chip}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1}>
                        <div className="overflow-hidden rounded-3xl border border-line">
                            <img
                                src={NET_IMG}
                                alt={c.career.netTitle}
                                className="aspect-[4/3] h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                                data-testid="career-network-image"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="container-x pb-28 md:pb-36" data-testid="career-openings">
                <Reveal>
                    <div className="flex flex-col items-center gap-6 rounded-3xl border border-line bg-paper p-12 text-center md:p-20">
                        <span className="flex h-16 w-16 items-center justify-center rounded-full border border-line bg-pure">
                            <Inbox className="h-7 w-7 text-sky-text" />
                        </span>
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-text">
                            {c.career.openLabel}
                        </p>
                        <h3 className="text-3xl font-light tracking-tight text-ink sm:text-4xl">
                            {c.career.openTitle}
                        </h3>
                        <p className="max-w-xl text-base leading-relaxed text-ink-soft">
                            {c.career.openBody}
                        </p>
                        <a
                            href={`mailto:${EMAIL}?subject=CV / Careers`}
                            data-testid="career-cv-cta"
                            className="mt-2 flex items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-semibold text-paper transition-colors hover:bg-sky-deep"
                        >
                            <Mail className="h-4 w-4" strokeWidth={2.5} />
                            {c.career.openCta} — {EMAIL}
                        </a>
                        <p className="mt-2 max-w-xl font-mono text-[11px] uppercase leading-relaxed tracking-[0.15em] text-mist">
                            {c.career.salesNote}{" "}
                            <a
                                href={`mailto:${EMAIL_SALES}`}
                                data-testid="career-sales-email"
                                className="text-sky-text underline decoration-sky-text/40 underline-offset-4 transition-colors hover:text-ink"
                            >
                                {EMAIL_SALES}
                            </a>
                        </p>
                    </div>
                </Reveal>
                <div className="mt-6 border-t border-line" data-testid="career-openings-list">
                    {c.career.openings.map((o, i) => (
                        <Reveal key={o.title} delay={i * 0.06}>
                            <div
                                className="grid gap-4 border-b border-line py-8 md:grid-cols-12 md:items-center"
                                data-testid={`career-opening-${i + 1}`}
                            >
                                <span className="font-light text-5xl text-mist/70 md:col-span-1">0{i + 1}</span>
                                <h3 className="text-2xl font-light tracking-tight text-ink md:col-span-5 sm:text-3xl">
                                    {o.title}
                                </h3>
                                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-soft md:col-span-3">
                                    <MapPin className="h-3.5 w-3.5 shrink-0 text-sky-text" />
                                    {o.location}
                                </p>
                                <div className="md:col-span-3 md:text-right">
                                    <a
                                        href={`mailto:${EMAIL}?subject=${encodeURIComponent(o.subject)}`}
                                        data-testid={`career-opening-apply-${i + 1}`}
                                        className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-paper transition-colors hover:bg-sky-deep"
                                    >
                                        <Mail className="h-3.5 w-3.5" strokeWidth={2.5} />
                                        {c.career.openApply}
                                    </a>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="bg-ink" data-testid="career-cta">
                <div className="container-x flex flex-col items-center gap-8 py-20 text-center md:flex-row md:justify-between md:text-left">
                    <MaskLines
                        lines={c.career.ctaBig}
                        className="text-3xl font-light tracking-tight text-paper sm:text-4xl"
                    />
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <a
                            href={`mailto:${EMAIL}?subject=CV / Careers`}
                            data-testid="career-cta-mail"
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-paper px-8 py-4 text-base font-semibold text-ink transition-colors hover:bg-sky"
                        >
                            <Mail className="h-4 w-4" />
                            {c.career.ctaMail}
                        </a>
                        <Link
                            to="/contact"
                            data-testid="career-cta-contact-link"
                            className="flex items-center justify-center gap-3 rounded-full border border-paper/30 px-8 py-4 font-mono text-sm text-paper transition-colors hover:border-paper hover:bg-paper/10"
                        >
                            {c.career.ctaContact}
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
